/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState, shallowEqual, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import propTypes from 'prop-types';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import { createSelector } from '@reduxjs/toolkit';
import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import { putAccount, globalAccountsView, globalTransactionViewPerCategory } from '../../redux/reducers/data';
import { setMessage } from '../../redux/actions/message';
// eslint-disable-next-line import/no-named-as-default
import TransactionsWrapper from './TransactionsWrapper';
// eslint-disable-next-line import/no-named-as-default
import QuotesWrapper from './QuotesWrapper';
import ImportFile from './import/ImportFile';
import AccountSettings from './AccountSettings';
import AlertModal from '../../elements/AlertModal';
import ChartWrapper from '../reports/ChartWrapper';
import AccountSum from '../../elements/AccountSum';
import UnlistedAddValuationWizard from './UnlistedAddValuationWizard';
import UnlistedCapitalTransactionWizard from './UnlistedCapitalTransactionWizard';
import getParamsByCategory from './params/getParamsByCategory';
import InputComponentAsyncCallback from '../../elements/InputComponentAsyncCallback';
import AttributeBar from './AttributeBar';
import ToolTipNoIcon from '../../elements/ToolTipNoIcon';
import { getArticleLink } from '../../blog';
import LoanIndicators from './LoanIndicators';
import PensionIndicators from './PensionIndicators';
import getButtonsToShow from './getButtonsToShow';
import { DEFAULT_TABLE_FILTERS } from './tableFilters';

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AccountDetails({ account: incomingAccount, setAccount, fileReceived, setFileReceived }) {
  // on mobile layouts, when dashboard is scrolled to the bottom, this component is rendered "above" the visible screen
  // as scrolling is disabled, the user cannot scroll to the top of the page -- so we have to do it for him
  // scroll to the top of the page (somehow it got rendered in the middle)
  const myRef = useRef(null);
  useEffect(() => {
    myRef.current.scrollIntoView({ behavior: 'instant' });
  }, []);

  // if anything changes in store.data, this component does not seem to receive the new data of the account object
  // keep the component initialised already with the account object and at the same time to receive all the updates
  // we will overwrite the account object with the store object for the given account.id
  const selectorAccount = createSelector(
    (state) => state.data[incomingAccount.category].accounts,
    (accounts) => accounts.find((item) => item.id === incomingAccount.id),
    {
      memoizeOptions: {
        // equalityCheck: (a, b) => a === b,
        // maxSize: 10,
        resultEqualityCheck: shallowEqual,
      },
    },
  );

  const dispatch = useDispatch();

  const selectAccount = useSelector(selectorAccount);
  if (!selectAccount) return true;
  const account = { ...incomingAccount, ...selectAccount };
  const { category, id } = account;
  const quoteAssetIds = useSelector(
    (state) => (!['stocks', 'metals', 'unlistedShares', 'crypto'].includes(category)
      ? []
      : [
        ...new Set(
          globalTransactionViewPerCategory(state, category)
            .filter((transaction) => transaction.accountId === id)
            .map((transaction) => transaction.assetId),
        ),
      ]),
    shallowEqual,
  );

  const baseCurrency = useSelector((state) => state.user.profile.settings.baseCurrency) || 'EUR';
  const allAccounts = useSelector(globalAccountsView) || [];
  const connectedAccounts = (allAccounts || []).filter((acc) => account.connectedDepositAccounts?.includes(acc.id));

  const { t: tAccountDetails, i18n } = useTranslation('accountDetails');
  const { t: tDashboard } = useTranslation('dashboard');

  // set table state | colWidths can be defined for Grid here
  const params = getParamsByCategory(category);
  const [tableState, setTableState] = useState({
    sortBy: 'date',
    sortDirectionAsc: category !== 'deposits',
    colWidths: params.colWidths,
    tableHeight: 300,
    filters: { ...DEFAULT_TABLE_FILTERS },
  });

  // used in Grid -- indicates that at least 1 change has been done by the user
  // including delete, which does not have a yellow background
  // TODO needed here for the X overlay close button
  const [userChangesPresent, setUserChangesPresent] = useState(false);

  // if you see fileReceived, go directly to import; otherwise initialise to table
  const [displayedComponent, setDisplayedComponent] = useState(fileReceived ? 'import' : 'table');
  const [displayedData, setDisplayedDataMode] = useState(getButtonsToShow(category, 'table', null)[0]); // other modes: 'quotes', 'settings', 'cashflow', pension & real estate do not have holdings,
  const [alert, setAlert] = useState({ visible: false });

  // handle user input from alert modal
  useEffect(() => {
    if (alert.id === 'aboutToLoseData') {
      if (alert.buttonClicked === 'ok') {
        setUserChangesPresent(false);
        if (typeof alert.onOk === 'function') {
          alert.onOk();
        } else {
          setAccount(null);
        }
      }
    }
  }, [alert.buttonClicked]);

  function handleClose(e) {
    if (displayedComponent !== 'table' && userChangesPresent) {
      setAlert({
        visible: true,
        id: 'aboutToLoseData',
        buttons: ['ok', 'cancel'],
        onOk: () => setAccount(null),
      });
    } else {
      setAccount(null);
    }
  }

  // create an event listener which calls setAlert when the user hits Esc when there are changes in the Grid view
  // DO NOT attempt to move it to the Redux alert, it won't work, as hooks cannot be used in listeners
  useEffect(() => {
    function handleEsc(e) {
      if (e.key === 'Escape') {
        if (displayedComponent !== 'table' && userChangesPresent) {
          setAlert((prevState) => ({
            ...prevState,
            visible: true,
            id: 'aboutToLoseData',
            buttons: ['ok', 'cancel'],
          }));
        } else {
          setAccount(null);
        }
      }
    }

    // if (displayedComponent === 'grid' || displayedComponent === 'table') {
    document.addEventListener('keydown', handleEsc, false);
    // }
    return () => {
      document.removeEventListener('keydown', handleEsc, false);
    };
  }, [{ userChangesPresent, displayedComponent }]); // it needs to be one object; if it is more, it creates several listeners

  async function inputFieldCallback({ propertyName, newValue }) {
    const accountObject = { ...account, [propertyName]: newValue };
    let result;
    try {
      result = await dispatch(putAccount({ data: accountObject, category }));
      if (result?.meta?.requestStatus === 'rejected') {
        throw new Error('accountCouldNotBeUpdated');
      }
      dispatch(setMessage('accountUpdatedSuccessfully'));
    } catch (err) {
      dispatch(setMessage('accountCouldNotBeUpdated'));
      throw new Error('accountCouldNotBeUpdated'); // the error is caught in the InputComponent
    }
    return result; // handled in InputComponent
  }

  function setNewState(arg) {
    switch (arg) {
      case 'holdings':
      case 'cashflow':
      case 'quotes':
      case 'settings':
        // some components are not available for certain data modes
        if (arg === 'cashflow') setDisplayedComponent('table'); // table is the only choice for cashlfow and holdings
        // settings only have the settings component
        if (arg === 'settings') setDisplayedComponent('settings');
        // holdings and quotes only have table and grid
        if (['holdings', 'quotes'].includes(arg) && !['table', 'grid'].includes(displayedComponent)) setDisplayedComponent('table');
        setDisplayedDataMode(arg);
        break;
      default: // handles table, quotes, import, wizard, columns, duplicate, previewer
        setDisplayedComponent(arg);
    }
  }

  // click handler for ButtonBar
  function handleClick(arg, options = {}) {
    if (userChangesPresent && !options.skipUnsavedChanges) {
      setAlert({
        visible: true,
        id: 'aboutToLoseData',
        buttons: ['ok', 'cancel'],
        onOk: () => setNewState(arg),
      });
    } else setNewState(arg);
  }

  // handles "go to deposits account" button on assets accounts (when cashflow component is empty)
  function handleNavigateToAccount(accountId) {
    const targetAccount = allAccounts.find((acc) => acc.id === accountId);
    if (targetAccount) {
      setAccount(targetAccount);
      setDisplayedComponent('table');
      setDisplayedDataMode('holdings');
    }
  }

  // used for cashflow data (filter all transactions to display only those linked to this account)
  function getCashflowFilter(accountId) {
    return (transaction) => transaction.tags?.assetId === accountId || transaction.tags?.accountId === accountId;
  }

  const standardProps = {
    account,
    setAccount,
    displayedComponent,
    displayedData,
    handleClick,
    connectedAccounts,
    handleNavigateToAccount,
  };

  const wrapperProps = {
    tableState,
    setTableState,
    userChangesPresent,
    setUserChangesPresent,
    highlightTransactionId: account.highlightTransactionId,
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div id="accountDetailsParent" ref={myRef} className="absolute w-full h-auto min-h-full z-50 top-0 left-0 md:p-16 bg-gray-300 bg-opacity-90 space-y-6" role="dialog">
      {/* FIXME used to ask "data will be lost" - validate height on mobile? */}
      {alert.visible ? <AlertModal alertId={alert.id} alertResetFunction={setAlert} resetCallbackObject={alert} buttons={alert.buttons || null} visible={alert.visible} /> : null}
      <div className="relative bg-gray-50 shadow xl:max-h-full 2xl:h-[90vh] px-4 py-5 sm:rounded-lg sm:px-8 sm:py-8 flex flex-col">
        {/* CLOSE + HELP BUTTONS */}

        {/* both these elements need z-70 to be above the header of account details */}
        <button id="account-details-close-button" type="button" className="absolute top-0 right-0 m-4 mb-6 cursor-pointer z-[70]">
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-500" onClick={handleClose} />
        </button>
        <a
          id="account-details-help-button"
          href={`/${i18n.language}/blog/${getArticleLink('account-details', i18n.language)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-0 right-0 m-4 mb-6 mr-12 cursor-pointer z-[70]"
        >
          <ToolTipNoIcon info={tAccountDetails('help')}>
            <QuestionMarkCircleIcon className="w-6 h-6 text-brandBlue-400 hover:text-brandBlue-500" onClick={() => {}} />
          </ToolTipNoIcon>
        </a>

        {/* HEADER */}

        {/* header section is not displayed for ColumnMatcher, DuplicateCheck and for Previewer */}
        {!['column', 'previewer', 'duplicate'].includes(displayedComponent) && (
          // this element overlaps with the Table setion below XS (this is sorted out in AttributeBar), so it has to be kept above it (it has z-50), so that it can be clicked
          <section aria-label="account header" className="z-[60] mt-2 sm:mt-0">
            {/* HEADER - BREADCRUMBS */}

            <div>
              <nav className="hidden sm:flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-4">
                  <li>
                    <div className="flex">
                      <div className="text-sm font-medium text-gray-400 hover:text-gray-500">{tDashboard(`categories.${category}`)}</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" />
                      <div aria-current="page" className="ml-4 text-sm font-medium text-gray-700 hover:text-gray-800">
                        {tAccountDetails('accountDetails')}
                      </div>
                    </div>
                  </li>
                </ol>
              </nav>
            </div>

            {/* HEADER - ACCOUNT NAME + ACCOUNT VALUE */}
            <div className="flex items-center justify-between pt-4 sm:pt-0">
              <div className="flex-1 min-w-0 flex items-center">
                <InputComponentAsyncCallback
                  parentValue={account.name}
                  stub="Account name?"
                  propertyName="name"
                  formatting="sm:-ml-[0.4rem] font-bold text-gray-900 text-2xl xs:text-2xl sm:text-3xl leading-tight"
                  callback={inputFieldCallback}
                  showButtons
                />
                <div className="sm:hidden w-full flex items-center">
                  {/* AttributeBar will conmpact itself to a button under sm breakpoint */}
                  <AttributeBar account={account} />
                </div>
              </div>
              {displayedComponent === 'table' && (
                <div className="flex-shrink-0 flex items-center md:mt-0 md:ml-4 z-10">
                  <p className="font-bold leading-tight text-gray-900 text-2xl xs:text-2xl sm:text-3xl">
                    <AccountSum
                      account={account}
                      category={category}
                      currencyCodeFormat="text-lg xs:text-lg sm:text-xl"
                      // for deposits, if the account is not in baseCurrency, we show the sum in accountCurrency in AccountDetails (and in baseCurrency on Tile)
                      showInBaseCurrency={!(category === 'deposits' && account.currency !== baseCurrency)}
                    />
                  </p>
                </div>
              )}
            </div>

            {/* ATTRIBUTE BAR */}

            <div className="hidden sm:block">
              {/* AttributeBar is in a different place below sm breakpoint  */}
              <AttributeBar account={account} />
            </div>
          </section>
        )}

        {/* CONTENTS */}

        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-8 h-full max-h-full lg:overflow-hidden">
          <div
            className={`${displayedComponent === 'table' ? 'xl:col-span-2' : 'xl:col-span-3'} ${
              displayedComponent === 'column' ? 'xl:h-[80vh]' : 'xl:h-[70vh]'
            } pt-2 flex flex-col items-start lg:overflow-hidden w-full`}
          >
            {['table', 'grid'].includes(displayedComponent) && displayedData === 'holdings' && <TransactionsWrapper {...standardProps} {...wrapperProps} />}
            {['table', 'grid'].includes(displayedComponent) && displayedData === 'cashflow' && (
              // special case: cashflow are always deposit transactions, so we need to pass "deposits" as category;
              // passing "filter" means to CategoryWrapperTransactions that it should filter by tags.accountId/assetId
              <TransactionsWrapper {...standardProps} {...wrapperProps} category="deposits" filter={getCashflowFilter(id)} />
            )}
            {['table', 'grid'].includes(displayedComponent) && displayedData === 'quotes' && <QuotesWrapper {...standardProps} {...wrapperProps} />}
            {displayedData === 'settings' ? <AccountSettings {...standardProps} /> : null}
            {['import', 'column', 'duplicate', 'previewer'].includes(displayedComponent) && (
              <ImportFile {...standardProps} {...wrapperProps} fileReceived={fileReceived} setFileReceived={setFileReceived} displayedData={displayedData} alert={alert} setAlert={setAlert} />
            )}
            {displayedComponent === 'wizard' && displayedData === 'quotes' && <UnlistedAddValuationWizard {...standardProps} {...wrapperProps} />}
            {displayedComponent === 'wizard' && displayedData === 'holdings' && <UnlistedCapitalTransactionWizard {...standardProps} {...wrapperProps} />}
          </div>

          {/* CHARTS  */}

          {displayedComponent === 'table' && (
            <section aria-labelledby="chart">
              <div className="rounded-md bg-white xl:overflow-hidden shadow sm:mt-4">
                <div className="p-6 pt-4">
                  <h2 className="text-base font-semibold text-gray-900" id="chart-title">
                    {['realEstate', 'objectsOfValue'].includes(category) ? tAccountDetails('last10years') : null}
                    {['deposits', 'stocks', 'metals', 'unlistedShares'].includes(category) ? tAccountDetails('last12months') : null}
                    {['loans', 'pension'].includes(category) ? tAccountDetails('stats') : null}
                  </h2>
                  {/* if the chart column needs to be taller, add the category here; otherwise it is fixed at h-96 */}
                  <div className={`mt-6 flex justify-center ${['loans', 'pension'].includes(category) ? 'min-h-[24rem]' : 'h-96'}`}>
                    {category === 'deposits' && (
                      <ChartWrapper
                        reportId="transactionsByMonthxx"
                        dataCallback={(data) => {
                          const thisAccountData = data.filter((item) => item.accountId === account.id);
                          const months = [...new Set(thisAccountData.map((tr) => dayjs.utc(tr.date).format('YYYY-MM')))].sort().reverse().slice(0, 12);
                          return thisAccountData.filter((item) => months.includes(dayjs.utc(item.date).format('YYYY-MM')));
                        }}
                      />
                    )}
                    {['stocks', 'metals', 'unlistedShares', 'crypto'].includes(category) && displayedData === 'quotes' && (
                      <ChartWrapper
                        reportId="quoteByYearLinexxxxxx"
                        dataCallback={(data = []) => data
                          .filter(
                            // eslint-disable-next-line max-len
                            (item) => quoteAssetIds.includes(item.assetId)
                              && item.source === 'manual'
                              && item.date > dayjs().subtract(category === 'stocks' ? 10 : 12, category === 'stocks' ? 'years' : 'months').valueOf()
                              && Number(item.date) === Number(item.quoteDate),
                          )
                          .map((item) => ({ ...item, displayName: item.symbol || item.displayName || item.assetId }))}
                        layoutOptions={{
                          axisBottom: { tickRotation: -90 },
                          margin: { bottom: 60 },
                        }}
                      />
                    )}
                    {category === 'stocks' && displayedData !== 'quotes' && (
                      <ChartWrapper
                        reportId="snapshotsByMonthxxxxx"
                        filterCallback={(item) => item.date > dayjs().subtract(12, 'months').valueOf() && item.accountId === account.id}
                        layoutOptions={{
                          startLabel: false,
                          endLabel: false,
                          margin: { left: 10, right: 60, bottom: 60, top: 30 },
                          axisBottom: {
                            format: (v) => dayjs(v).format('MM-YYYY'),
                            tickRotation: -90,
                          },
                        }}
                      />
                    )}
                    {(category === 'realEstate' || category === 'objectsOfValue') && displayedData === 'quotes' && (
                      // displays value of the object over time
                      <ChartWrapper
                        reportId="quoteByYearLinexxxxxx"
                        filterCallback={(item) => item.assetId === account.id && item.date > dayjs().subtract(10, 'years').valueOf()}
                        layoutOptions={{
                          axisBottom: { tickRotation: -90 },
                          margin: { bottom: 60 },
                        }}
                      />
                    )}
                    {category === 'objectsOfValue' && displayedData === 'holdings' && (
                      // displays holdings over time
                      <ChartWrapper
                        reportId="realEstateByMonthxxxx"
                        filterCallback={(item) => item.assetId === account.id && item.date > dayjs().subtract(10, 'years').valueOf()}
                        layoutOptions={{
                          axisBottom: { tickRotation: -90 },
                          margin: { bottom: 60, right: 10, left: 60, top: 30 },
                        }}
                      />
                    )}
                    {category === 'loans' && <LoanIndicators accountId={account.id} />}
                    {category === 'pension' && <PensionIndicators accountId={account.id} />}
                  </div>
                  <div className="mt-6">
                    <Link
                      to={`/${i18n.language}/app/reports`}
                      className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {tAccountDetails('goToReports')}
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
AccountDetails.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  account: propTypes.object.isRequired,
  setAccount: propTypes.func.isRequired,
  fileReceived: propTypes.oneOf([
    propTypes.shape({
      fileName: propTypes.string.isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      fileData: propTypes.any,
    }),
    null,
  ]),
  setFileReceived: propTypes.func,
};
AccountDetails.defaultProps = {
  fileReceived: null,
  setFileReceived: null,
};
